import { FormattedMessage, useIntl } from "react-intl";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  AccountMessage,
  AccountMessageLink,
  Fieldset,
  Form,
  Title,
} from "./SignupStyles";

import countryList from "../../../../../../shared/resources/_country.json";
import ConfigService from "./service/ConfigService";

import { getCountry, getLanguage } from "./config/_localeMessages";

import Input from "./ui/Input";
import Select from "./ui/Select";
import Button from "./ui/Button";
import OAuthButtons from "./OAuthButtons/OAuthButtons";
import ErrorMessage from "@/components/FormElements/ErrorMessage.jsx";
import CSRFInput from "@/components/CSRFInput.jsx";
import { useSearchParams } from "react-router-dom";

const SIGNUP_STASH_KEY = "signup-form-stash";

// _country.json is hand-ordered: these key markets pinned first, then the
// rest alphabetized in English. The pinned head keeps its order in every
// language; the tail is re-sorted per locale once labels are translated.
const PINNED_COUNTRIES = ["US", "CA", "AU", "IT", "GB", "NZ", "DE", "AT", "FR"];

const SignUp = (props) => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [serverError, setServerError] = useState(false);

  const [html5error, setHtml5error] = useState({});
  const [validated, setValidated] = useState(false);
  const [country, setCountry] = useState("US");

  const configService = ConfigService.getInstance();
  const config = configService.get();

  const firstNameLabel = intl.formatMessage({
    id: "template.first_name",
    defaultMessage: "First Name",
  });
  const lastNameLabel = intl.formatMessage({
    id: "template.last_name",
    defaultMessage: "Last Name",
  });
  const emailLabel = intl.formatMessage({
    id: "template.email",
    defaultMessage: "Email",
  });
  const passwordLabel = intl.formatMessage({
    id: "template.password",
    defaultMessage: "Password",
  });
  const confirmPasswordLabel = intl.formatMessage({
    id: "template.confirm_password",
    defaultMessage: "Confirm Password",
  });

  const countryOptions = useMemo(() => {
    const collator = new Intl.Collator(intl.locale);
    const toOption = (country) => ({
      value: country.id,
      label: intl.formatMessage({
        id: `country.${country.id}`,
        defaultMessage: country.label,
      }),
    });
    const available = countryList.filter((country) => country.available);
    const pinned = available
      .filter((country) => PINNED_COUNTRIES.includes(country.id))
      .map(toOption);
    const rest = available
      .filter((country) => !PINNED_COUNTRIES.includes(country.id))
      .map(toOption)
      .sort((a, b) => collator.compare(a.label, b.label));
    return [...pinned, ...rest];
  }, [intl]);

  const errorMsg = searchParams.get("error");
  const message = searchParams.get("message");

  const successRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        // first geolocate the country
        if (sessionStorage.getItem("country")) {
          setCountry(sessionStorage.getItem("country"));
        } else {
          const location = await ConfigService.getInstance().getUserLocation();
          sessionStorage.setItem("country", location?.location?.countryCode);
          setCountry(location?.location?.countryCode);
        }
      } catch (ex) {

      }
    })();

  }, []);

  useEffect(() => {
    document.title = intl.formatMessage({
      id: "auth.docTitleSignup",
      defaultMessage: "Create Account - NewsGuard",
    });
  }, [intl]);

  // On a successful registration the server redirects back with ?message=…;
  // move focus to the confirmation so screen readers announce it on load.
  useEffect(() => {
    if (message) successRef.current?.focus();
  }, [message]);

  const [formFields, setFormFields] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      firstName: "",
      lastName: "",
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      country: country || getCountry() || "US",
      language: getLanguage() || config.language || "en",
    },
  );

  // Registration is a native form POST; on failure (e.g. email already
  // registered) the server redirects back with ?error=… and the reload wipes
  // all field state. Stash the non-sensitive fields before submitting and
  // restore them when we land back here with an error, so the user only has
  // to re-enter their password. The stash is one-shot: cleared on read so a
  // later fresh visit starts blank.
  useEffect(() => {
    const stash = sessionStorage.getItem(SIGNUP_STASH_KEY);
    if (stash) {
      sessionStorage.removeItem(SIGNUP_STASH_KEY);
      if (errorMsg) {
        try {
          setFormFields(JSON.parse(stash));
        } catch {
          // Corrupted stash — ignore and start blank.
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (event) => {
    event.target.setCustomValidity(""); // have to reset custom validity else field never returns to valid
    const target = event.target;
    const inputName = target.name;
    let inputValue = target.value;
    if (inputName === "optIn") {
      inputValue = target.checked;
    }

    setHtml5error((prevState) => ({
      ...prevState,
      [inputName]: undefined,
    }));

    setFormFields({ [inputName]: inputValue });
  };

  const handleSubmit = async (event) => {
    setLoading(true);
    setError(null);
    setHtml5error({});

    event.preventDefault();
    if (validate(event)) {
      const { password, confirmPassword, ...safeFields } = formFields;
      sessionStorage.setItem(SIGNUP_STASH_KEY, JSON.stringify(safeFields));
      event.target.submit();
    } else {
      setLoading(false);
      // Move focus to the first invalid field (noValidate suppresses the
      // browser's native focus-first-error behavior).
      event.target.querySelector(":invalid")?.focus();
    }
  };

  const validateConfirmPassword = () => {
    const passwordField = document.querySelector("#password");
    const confirmPasswordField = document.querySelector("#confirmPassword");
    if (confirmPasswordField.value !== passwordField.value) {
      // setCustomValidity takes a string; a non-empty value marks the field
      // invalid so checkValidity()/submit are blocked until it matches.
      confirmPasswordField.setCustomValidity(
        "Please enter matching passwords for Password and Confirm Password",
      );
      setHtml5error((prevState) => ({
        ...prevState,
        confirmPassword: {
          id: "message.password_confirm_instruction",
          defaultMessage:
            "Please enter matching passwords for Password and Confirm Password",
          name: "confirmPassword",
          html: true,
        },
      }));
    } else {
      confirmPasswordField.setCustomValidity("");
    }
  };

  const checkIsValid = (e) => {
    if (e.target.checkValidity() === false) {
      e.target.parentNode.classList.add("was-validated");
    }
    if (e.target.id === "confirmPassword") {
      validateConfirmPassword();
    }
  };

  const validate = (event) => {
    setValidated(true);

    return event.target.checkValidity();
  };

  const handleSignin = (e) => {
    e.preventDefault();

    // clientUrl is per-environment and loaded lazily; resolve it before
    // leaving the page.
    configService.load().then(() => {
      const returnUrl =
        configService.get().clientUrl || "https://www.newsguard.ai";
      window.location.href = returnUrl + "/signin";
    });
  };

  return (
    <div className="600:w-[480px] mx-auto mt-12 flex flex-col pb-12 px-3xl">
      {message ? (
        <div className="mb-10">
          {/* The registration fallback in HomeController (no saved OAuth
              request to resume) redirects here with its English-only
              "Registration Success" — map that one to our localized copy,
              and pass any other (future) server message through verbatim. */}
          <Title
            ref={successRef}
            tabIndex={-1}
            role="status"
            className="outline-none"
          >
            {message === "Registration Success" ? (
              <FormattedMessage
                id="template.complete.title"
                defaultMessage="You're all set!"
              />
            ) : (
              message
            )}
          </Title>
          <form
            //action={configService.formulateContextPath("/logout")}
            method="POST"
            onSubmit={handleSignin}
          >
            <Button className="mb-3 w-full" type="submit">
              <FormattedMessage id="template.signin" defaultMessage="Sign in" />
            </Button>
          </form>
        </div>
      ) : (
        <>
          <Title>
            <FormattedMessage
              id="template.createAccount"
              defaultMessage="Create Account"
            />
          </Title>
          <p className="text-center text-med">
            <FormattedMessage
              id="auth.signupIntro"
              defaultMessage="Create your free account to access NewsGuard AI and get news on any topic from 12,000+ reliable sources"
            />
          </p>
          <OAuthButtons />
          <p className="text-center my-4 lowercase">
            <FormattedMessage id="template.or" defaultMessage="OR" />
          </p>
          <ErrorMessage error={errorMsg} />
          <Form
            className={`${validated ? "was-validated" : ""}`}
            onSubmit={handleSubmit}
            noValidate
            id="signin-form"
            method="post"
          >
            <Fieldset className="flex flex-col gap-xl">
              <Input
                type="text"
                id="firstName"
                name="firstName"
                formField
                label={firstNameLabel}
                placeholder={firstNameLabel}
                autoComplete="given-name"
                value={formFields.firstName}
                onChange={handleInputChange}
                onBlur={checkIsValid}
                required={true}
                maxLength={35}
                onInvalid={() => {
                  let firstName = {
                    id: "message.firstname_blank",
                    defaultMessage: "Please enter a first name",
                    name: "firstName",
                    html: true,
                  };

                  setHtml5error((prevState) => ({
                    ...prevState,
                    firstName,
                  }));
                }}
                error={html5error.firstName}
              />
              <Input
                type="text"
                id="lastName"
                name="lastName"
                formField
                label={lastNameLabel}
                placeholder={lastNameLabel}
                autoComplete="family-name"
                value={formFields.lastName}
                onChange={handleInputChange}
                onBlur={checkIsValid}
                required={true}
                maxLength={35}
                onInvalid={() => {
                  let lastName = {
                    id: "message.lastname_blank",
                    defaultMessage: "Please enter a last name",
                    name: "lastName",
                    html: true,
                  };

                  setHtml5error((prevState) => ({
                    ...prevState,
                    lastName,
                  }));
                }}
                error={html5error.lastName}
              />
              <Select
                id="country"
                name="country"
                label={intl.formatMessage({
                  id: "template.country",
                  defaultMessage: "Country",
                })}
                required
                autoComplete="country"
                value={formFields.country}
                onChange={handleInputChange}
                options={countryOptions}
              />
              <Input
                type="email"
                id="email"
                name="email"
                formField
                label={emailLabel}
                placeholder={emailLabel}
                value={formFields.email}
                onChange={handleInputChange}
                onBlur={checkIsValid}
                autoComplete="email"
                required={true}
                onInvalid={() => {
                  let email = {
                    id: "message.email_invalid",
                    defaultMessage: "Please enter a correct email address",
                    name: "email",
                    html: true,
                  };
                  setHtml5error((prevState) => ({
                    ...prevState,
                    email,
                  }));
                }}
                error={html5error.email || error}
              />

              <Input
                type="password"
                id="password"
                name="password"
                formField
                label={passwordLabel}
                placeholder={passwordLabel}
                value={formFields.password}
                onChange={handleInputChange}
                onBlur={checkIsValid}
                autoComplete="new-password"
                minLength={8}
                maxLength={50}
                required={true}
                onInvalid={() => {
                  let password = {
                    id: "message.password_length_instruction",
                    defaultMessage:
                      "Please enter a password that is 8 characters or longer",
                    name: "password",
                    html: true,
                  };
                  setHtml5error((prevState) => ({
                    ...prevState,
                    password,
                  }));
                }}
                error={html5error.password}
              />

              <Input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                formField
                label={confirmPasswordLabel}
                placeholder={confirmPasswordLabel}
                value={formFields.confirmPassword}
                onChange={handleInputChange}
                onBlur={checkIsValid}
                required={true}
                autoComplete="new-password"
                onInvalid={() => {
                  let confirmPassword = {
                    id: "message.password_confirm_instruction",
                    defaultMessage: "Please enter matching passwords",
                    name: "confirmPassword",
                    html: true,
                  };
                  setHtml5error((prevState) => ({
                    ...prevState,
                    confirmPassword,
                  }));
                }}
                error={html5error.confirmPassword}
              />

              {error ? <div className="form__error">{errorMessage}</div> : ""}

              <p className="-mt-md mb-lg text-left text-xs italic text-text-placeholder-light dark:text-text-placeholder-dark">
                <FormattedMessage
                  id="template.all_field_req"
                  defaultMessage="All fields are required"
                />
              </p>
              <Button type="submit" isLoading={loading} className="mb-3 w-full">
                <FormattedMessage id="template.next" defaultMessage="Next" />
              </Button>
              <AccountMessage>
                <FormattedMessage
                  id="auth.alreadyHaveAccount"
                  defaultMessage="Already have an account?"
                />{" "}
                <AccountMessageLink
                  to={configService.formulateContextPath("/login")}
                >
                  <FormattedMessage id="template.signin" defaultMessage="Sign in" />
                </AccountMessageLink>
              </AccountMessage>
            </Fieldset>
            <CSRFInput />
          </Form>
        </>
      )}
    </div>
  );
};

export default SignUp;
